import React, { ReactElement, useMemo, useCallback } from "react";
import { QualityHubTaskGroupFormProps } from "./QualityHubTaskGroupForm.types";
import { FormSection, FormControl } from "src/ui/components/Forms";
import { localize } from "src/l10n";
import { TextField, Checkbox } from "@fluentui/react";
import SpintrDatePicker from "src/spintr/components/SpintrDatePicker";
import { Conditional } from "src/components/Conditional";
import { CaptionBody } from "src/components/Text";

function dateToTimeString(date: Date): string | undefined {
    if (!date || !(date instanceof Date)) {
        return undefined;
    }

    return date.getHours().toString().padStart(2, "0") + ":" +
           date.getMinutes().toString().padStart(2, "0") + ":" +
           date.getSeconds().toString().padStart(2, "0");
}

function timeStringToDate(time: string): Date | undefined {
    if (!time || time.length === 0) {
        return undefined;
    }

    const parts = time.split(":");

    if (parts.length !== 3) {
        return undefined;
    }

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parseInt(parts[2].substr(0, 2), 10);

    const date = new Date();
    date.setHours(hours, minutes, seconds, 0);

    return date;
}

/**
 * QualityHubTaskGroupForm component
 *
 * @param {QualityHubTaskGroupFormProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubTaskGroupForm({
    isCustomizing = false,
    model,
    onModelChange,
}: QualityHubTaskGroupFormProps): ReactElement | null {
    const l10n = useMemo(() => ({
        name: localize("Namn"),
        keyConsideration: localize("QH_KEY_CONSIDERATION"),
        start: localize("Starttid"),
        end: localize("Sluttid"),
        timeManagement: localize("QH_TIME_MANAGEMENT"),
        timeManagementInfo: localize("QH_TIME_MANAGEMENT_INFO"),
    }), []);

    const onNameChanged = useCallback(
        (_: unknown, newValue?: string) => onModelChange((model) => ({
            ...model,
            name: newValue ?? "",
        })),
        [onModelChange]);

    const onKeyConsiderationChanged = useCallback(
        (_: unknown, newValue?: string) => onModelChange((model) => ({
            ...model,
            keyConsideration: newValue ?? "",
        })),
        [onModelChange]);
    
    const onStartTimeChanged = useCallback(
        (date: Date) => onModelChange((model) => ({
            ...model,
            startTime: dateToTimeString(date),
        })),
        [onModelChange]);

    const onEndTimeChanged = useCallback(
        (date: Date) => onModelChange((model) => ({
            ...model,
            endTime: dateToTimeString(date),
        })),
        [onModelChange]);

    const onAllowOverrideChanged = useCallback(
        (_: unknown, checked?: boolean) => onModelChange((model) => ({
            ...model,
            allowOverride: !!checked,
        })),
        [onModelChange]);

    return (
        <div className="QualityHubTaskGroupForm">
            <FormSection>
                <FormControl>
                    <TextField aria-required={true}
                               className="textField"
                               label={l10n.name}
                               name="name"
                               onChange={onNameChanged}
                               required={true}
                               value={model.name} />
                </FormControl>
            </FormSection>

            <FormSection>
                <FormControl>
                    <TextField aria-required={false}
                               className="textField"
                               label={l10n.keyConsideration}
                               name="keyConsideration"
                               onChange={onKeyConsiderationChanged}
                               required={false}
                               value={model.keyConsideration} />
                </FormControl>
            </FormSection>

            <FormSection title={l10n.timeManagement}>
                <CaptionBody className="time-management-info">
                    {l10n.timeManagementInfo}
                </CaptionBody>

                <div className="QualityHubTaskGroupForm-timeSection">
                    <FormControl className="QualityHubTaskGroupForm-startTime">
                        <SpintrDatePicker isClearable={true}
                                          label={l10n.start}
                                          onChangeHandler={onStartTimeChanged}
                                          showTimeSelectOnly={true}
                                          value={timeStringToDate(model.startTime)} />
                    </FormControl>

                    <FormControl className="QualityHubTaskGroupForm-endTime">
                        <SpintrDatePicker isClearable={true}
                                          label={l10n.end}
                                          onChangeHandler={onEndTimeChanged}
                                          showTimeSelectOnly={true}
                                          value={timeStringToDate(model.endTime)} />
                    </FormControl>
                </div>
            </FormSection>

            <FormSection title={localize("Installningar")}>

                <Conditional condition={!isCustomizing}>
                        <Checkbox checked={model.allowOverride}
                                  label={localize("ALLOW_CUSTOMIZATION")}
                                  name="allowOverride"
                                  onChange={onAllowOverrideChanged} />
                </Conditional>
            </FormSection>
        </div>
    );
}

export default QualityHubTaskGroupForm;

